import React, { useEffect, useState } from "react";
import { Form, Button, Spinner } from "@themesberg/react-bootstrap";
import { proformaSchema } from "../../yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { fetchStatesData, getContactGroup } from "../../service/api";

const yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const columns = [
  { label: "Proforma Name", name: "proformaName" },
  { label: "Scenario Name", name: "scenarioName" },
  {
    label: "Adjusted Gross Income",
    name: "adjustedGrossIncome",
    type: "number",
  },
  { label: "Equity Contribution", name: "equityContribution", type: "number" },
  { label: "Loss Limitation", name: "lossLimitation", type: "select" },
  { label: "Contact Group", name: "contactGroup", type: "apiSelect" },
  {
    label: "Federal Revenue Spread",
    name: "federalRevenueSpread",
    type: "select",
  },
  { label: "State", name: "state", type: "apiSelect" },
  {
    label: "Federal Marginal Tax Rate",
    name: "federalMarginalTaxRate",
    type: "number",
  },
  { label: "Internal Markup %", name: "internalMarkup", type: "number" },
  { label: "Affiliate Markup %", name: "affiliateMarkup", type: "number" },
  { label: "Client Name", name: "clientName" },
  { label: "Client Email Address", name: "clientEmailAddress" },
  { label: "ITC % Target", name: "itcTarget", type: "number" },
  { label: "Proforma GDrive Link", name: "proformaGDrive" },
  { label: "Client Proforma URL", name: "clientProformaUrl" },
  { label: "Affiliate Markup % EC", name: "affiliateMarkupEC", type: "number" },
];

// Fetch functions outside useEffect
const fetchStatesOptions = async () => {
  const statesRes = await fetchStatesData();
  return statesRes.data.data.map((s) => ({ value: s.state, label: s.state }));
};

const fetchContactGroupOptions = async () => {
  const groupsRes = await getContactGroup();
  return groupsRes.data.data.map((g) => ({
    value: g.contactGroupName,
    label: g.contactGroupName,
  }));
};

export const ProformaForm = ({
  onSubmit,
  onCancel,
  submitLoading,
  defaultValues,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(proformaSchema),
    defaultValues,
  });

  const [statesOptions, setStatesOptions] = useState([]);
  const [contactGroupOptions, setContactGroupOptions] = useState([]);

  const loadOptions = async () => {
    try {
      const [states, groups] = await Promise.all([
        fetchStatesOptions(),
        fetchContactGroupOptions(),
      ]);
      setStatesOptions(states);
      setContactGroupOptions(groups);
    } catch (err) {
      console.error("Error fetching states or contact groups", err);
    }
  };
  useEffect(() => {
    loadOptions();
  }, []);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      {columns.map((field) => {
        // Yes/No select fields
        if (field.type === "select") {
          return (
            <Form.Group className="mb-3" key={field.name}>
              <Form.Label>{field.label}</Form.Label>
              <Controller
                name={field.name}
                control={control}
                render={({ field: controllerField }) => (
                  <Select
                    {...controllerField}
                    options={yesNoOptions}
                    placeholder={`Select ${field.label}`}
                    value={yesNoOptions.find(
                      (option) => option.value === controllerField.value
                    )}
                    onChange={(val) => controllerField.onChange(val.value)}
                  />
                )}
              />
              {errors[field.name] && (
                <div className="text-danger mt-1">
                  {errors[field.name]?.message}
                </div>
              )}
            </Form.Group>
          );
        }

        // API select fields
        if (field.type === "apiSelect") {
          const options =
            field.name === "state" ? statesOptions : contactGroupOptions;
          return (
            <Form.Group className="mb-3" key={field.name}>
              <Form.Label>{field.label}</Form.Label>
              <Controller
                name={field.name}
                control={control}
                render={({ field: controllerField }) => (
                  <Select
                    {...controllerField}
                    options={options}
                    placeholder={`Select ${field.label}`}
                    value={options.find(
                      (option) => option.value === controllerField.value
                    )}
                    onChange={(val) => controllerField.onChange(val.value)}
                  />
                )}
              />
              {errors[field.name] && (
                <div className="text-danger mt-1">
                  {errors[field.name]?.message}
                </div>
              )}
            </Form.Group>
          );
        }

        // Regular input fields
        return (
          <Form.Group className="mb-3" key={field.name}>
            <Form.Label>{field.label}</Form.Label>
            <Form.Control
              type={field.type || "text"}
              {...register(field.name)}
              placeholder={`Enter ${field.label}`}
              isInvalid={!!errors[field.name]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[field.name]?.message}
            </Form.Control.Feedback>
          </Form.Group>
        );
      })}

      <div className="d-flex justify-content-end mt-4">
        <Button variant="primary" onClick={onCancel} className="me-2">
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {submitLoading ? <Spinner size="sm" animation="border" /> : "Submit"}
        </Button>
      </div>
    </Form>
  );
};
